import React from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronRight, Minus, ShieldCheck, Sparkles, X } from "lucide-react";
import { formatMoney } from "../../lib/api.js";
import type { BillingInterval, Plan, PlanComparisonGroup } from "../../../../shared/types.js";
import { currencyFromLocale, getAnnualInstallmentCents, getAnnualSavings, getPlanPricing } from "../../../../shared/plans.js";
import { STONE_INSTALLMENT_COUNT } from "../../../../shared/stone.js";
import {
  PLAN_COMPARISON_GROUPS,
  getPlanComparisonRowsByGroup,
  getPlanComparisonValue,
} from "../../../../shared/planComparison.js";
import {
  formatPlanComparisonCellValue,
  planComparisonCellIsPositive,
} from "../../../../shared/formatPlanComparison.js";

export type PlanComparisonMode = "marketing" | "integrated";

const PLAN_ORDER: Plan[] = ["basic", "premium"];

export interface PlanComparisonTableProps {
  interval: BillingInterval;
  mode?: PlanComparisonMode;
  /** Selected plan in integrated selector mode. */
  selectedPlan?: Plan;
  /** Integrated flow: click column header to select plan. */
  onSelectPlan?: (plan: Plan) => void;
  /**
   * Marketing flow: the plans page shows prices and its main CTAs in PlanChoiceCards above the
   * table; here a last row repeats the choice so nobody scrolls back up after reading.
   */
  onChoosePlan?: (plan: Plan) => void;
  /**
   * Marketing flow: tapping a plan's column header chooses it. The header stays on screen while
   * the buyer scrolls, and buyers tapped its name and price expecting exactly that (PIT-457).
   */
  onChooseFromHeader?: (plan: Plan) => void;
  /** Integrated flow: marks current subscription plan + interval. */
  currentPlan?: Plan;
  currentInterval?: BillingInterval;
  /** Price delta vs current plan (same interval), shown when column is selected. */
  priceDeltaByPlan?: Partial<Record<Plan, number>>;
  disabled?: boolean;
  /** Offer path: hide the other plan (case 1 locks Premium). */
  lockedPlan?: Plan;
  /** First-month offer: both plans charge this amount today. */
  offerFirstCents?: number;
}

export function PlanComparisonTable({
  interval,
  mode = "marketing",
  selectedPlan,
  onSelectPlan,
  onChoosePlan,
  onChooseFromHeader,
  currentPlan,
  currentInterval,
  priceDeltaByPlan,
  disabled = false,
  lockedPlan,
  offerFirstCents,
}: PlanComparisonTableProps) {
  const { t } = useTranslation();
  const isIntegrated = mode === "integrated";
  const visiblePlans = lockedPlan ? PLAN_ORDER.filter((plan) => plan === lockedPlan) : PLAN_ORDER;

  return (
    <div className={`plan-comparison ${isIntegrated ? "plan-comparison-integrated" : "plan-comparison-marketing"}`}>
      <div
        className={`plan-comparison-top${!isIntegrated && visiblePlans.length === 1 ? " plan-comparison-top-single" : ""}`}
        role="row"
      >
        <div className="plan-comparison-top-spacer" aria-hidden />
        {visiblePlans.map((plan) => (
          <PlanComparisonColumnHeader
            key={plan}
            plan={plan}
            interval={interval}
            mode={mode}
            featured={plan === "premium"}
            selected={selectedPlan === plan}
            isCurrent={
              !!currentPlan &&
              !!currentInterval &&
              plan === currentPlan &&
              interval === currentInterval
            }
            priceDeltaCents={priceDeltaByPlan?.[plan]}
            disabled={disabled}
            onSelect={onSelectPlan ? () => onSelectPlan(plan) : undefined}
            onChoose={!isIntegrated && onChooseFromHeader ? () => onChooseFromHeader(plan) : undefined}
            offerFirstCents={offerFirstCents}
          />
        ))}
      </div>

      <div className="plan-comparison-scroll">
        <table className="plan-comparison-table" role="table">
          <caption className="sr-only">{t("plansPage.compareTitle")}</caption>
          {!isIntegrated && (
            // Plans page: fixed column widths, the same as the header's grid above.
            <colgroup>
              <col className="plan-comparison-col-feature" />
              {visiblePlans.map((plan) => (
                <col key={plan} />
              ))}
            </colgroup>
          )}
          <tbody>
            {PLAN_COMPARISON_GROUPS.map((group: PlanComparisonGroup) => (
              <React.Fragment key={group}>
                <tr className="plan-comparison-group-row">
                  <th scope="colgroup" colSpan={visiblePlans.length + 1}>
                    {t(`planComparison.groups.${group}`)}
                  </th>
                </tr>
                {getPlanComparisonRowsByGroup(group).map((row, rowIndex) => (
                  <tr
                    key={row.id}
                    className={rowIndex % 2 === 0 ? "plan-comparison-row-even" : "plan-comparison-row-odd"}
                  >
                    <th scope="row" className="plan-comparison-feature-col">
                      <span className="plan-comparison-feature-label">
                        {t(row.labelKey)}
                        {row.comingSoon ? (
                          <span className="plan-comparison-coming-soon">
                            {t("planComparison.comingSoon")}
                          </span>
                        ) : null}
                      </span>
                    </th>
                    {visiblePlans.map((plan) => (
                      <PlanComparisonCell
                        key={`${row.id}-${plan}`}
                        value={getPlanComparisonValue(row, plan)}
                        displayType={row.displayType}
                        featured={plan === "premium"}
                        selected={selectedPlan === plan}
                      />
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {!isIntegrated && onChoosePlan && (
        // Its own grid with the header's columns, not a table row: a button can't shrink to the
        // plan column's width on a phone, and would widen the table under the header.
        <div className="plan-comparison-top plan-comparison-choose">
          <div className="plan-comparison-top-spacer" aria-hidden />
          {visiblePlans.map((plan) => (
            <div key={`choose-${plan}`} className="plan-comparison-choose-cell">
              <button
                type="button"
                className={`plan-comparison-cta ${plan === "premium" ? "plan-comparison-cta-primary" : "plan-comparison-cta-secondary"}`}
                onClick={() => onChoosePlan(plan)}
                disabled={disabled}
              >
                {t("planComparison.chooseShort", { plan: t(`plans.${plan}`) })}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Risk reversal at the moment of choice, right under the plan prices: a seal, not a footnote. */
export function PlanGuaranteeSeal() {
  const { t } = useTranslation();
  return (
    <div className="plan-comparison-guarantee">
      <ShieldCheck className="plan-comparison-guarantee-icon" aria-hidden />
      <div>
        <div className="plan-comparison-guarantee-title">{t("planComparison.guaranteeTitle")}</div>
        <div className="plan-comparison-guarantee-body">{t("planComparison.guaranteeBody")}</div>
      </div>
    </div>
  );
}

function PlanComparisonColumnHeader({
  plan,
  interval,
  mode,
  featured,
  selected,
  isCurrent,
  priceDeltaCents,
  disabled,
  onSelect,
  onChoose,
  offerFirstCents,
}: {
  plan: Plan;
  interval: BillingInterval;
  mode: PlanComparisonMode;
  featured?: boolean;
  selected?: boolean;
  isCurrent?: boolean;
  priceDeltaCents?: number;
  disabled?: boolean;
  onSelect?: () => void;
  /** Marketing only: the header chooses the plan, like the "Escolher" buttons. */
  onChoose?: () => void;
  offerFirstCents?: number;
}) {
  const { t, i18n } = useTranslation();
  const currency = currencyFromLocale(i18n.language);
  const money = (cents: number) => formatMoney(cents, i18n.language, currency);
  const planLabel = t(`plans.${plan}`);
  const isIntegrated = mode === "integrated";
  const isSelectable = isIntegrated && !!onSelect;
  const isChoosable = !isIntegrated && !!onChoose;
  // Integrated: the seal follows the selection. Plans page: Premium always carries it, so the
  // recommended column reads as the answer while the buyer scrolls the rows.
  const showFeaturedBadge = featured && (isIntegrated ? selected : true);
  const showPriceDelta =
    isIntegrated &&
    selected &&
    priceDeltaCents !== undefined &&
    priceDeltaCents !== 0 &&
    !isCurrent;

  const headerClass = [
    "plan-comparison-top-col",
    featured ? "plan-comparison-top-col-featured" : "",
    selected ? "plan-comparison-top-col-selected" : "",
    isCurrent && selected ? "plan-comparison-top-col-current" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const headerBody = (
    <>
      {showFeaturedBadge && (
        <span className="plan-comparison-seal">
          <Sparkles className="plan-comparison-seal-icon" aria-hidden />
          {t("plans.recommended")}
        </span>
      )}
      {!isIntegrated && !showFeaturedBadge && (
        // An invisible seal: keeps this column's name, price and "Escolher" level with the
        // recommended column's, which carries the real one.
        <span className="plan-comparison-seal plan-comparison-seal-slot" aria-hidden>
          {t("plans.recommended")}
        </span>
      )}
      {isCurrent && selected && (
        <span className="plan-comparison-badge plan-comparison-badge-current">
          {t("planComparison.currentPlanBadge")}
        </span>
      )}
      <div className="plan-comparison-plan-name">{planLabel}</div>
      {isIntegrated && <PlanPriceLines plan={plan} interval={interval} offerFirstCents={offerFirstCents} />}
      {!isIntegrated && (
        <>
          <div id={`plan-comparison-${plan}-tagline`} className="plan-comparison-tagline">
            {t(`planComparison.taglines.${plan}`)}
          </div>
          {/* Amount on its own line and the period small under it, so the column is as narrow as
              the table's plan column below and the two line up while the rows scroll. */}
          <div id={`plan-comparison-${plan}-price`} className="plan-comparison-head-price">
            {offerFirstCents != null ? (
              <span className="plan-comparison-head-amount">{money(offerFirstCents)}</span>
            ) : interval === "annual" && currency === "BRL" ? (
              <>
                <span className="plan-comparison-head-amount">{money(getAnnualInstallmentCents(plan, currency))}</span>{" "}
                <span className="plan-comparison-head-period">
                  {t("planComparison.headInstallments", { count: STONE_INSTALLMENT_COUNT })}
                </span>
              </>
            ) : (
              <>
                <span className="plan-comparison-head-amount">
                  {money(getPlanPricing(plan, interval, currency).totalCents)}
                </span>
                <span className="plan-comparison-head-period">
                  {t(interval === "annual" ? "plans.perYear" : "plans.perMonth")}
                </span>
              </>
            )}
          </div>
        </>
      )}
      {showPriceDelta && (
        <div
          className={`plan-comparison-delta ${priceDeltaCents! > 0 ? "plan-comparison-delta-more" : "plan-comparison-delta-less"}`}
        >
          {priceDeltaCents! > 0
            ? t("planComparison.priceDeltaMore", { amount: money(priceDeltaCents!) })
            : t("planComparison.priceDeltaLess", { amount: money(Math.abs(priceDeltaCents!)) })}
          {interval === "monthly" ? ` ${t("plans.perMonth")}` : t("plans.perYear")}
        </div>
      )}
      {isIntegrated && isSelectable && selected && (
        <span className="plan-comparison-selected-indicator" aria-hidden />
      )}
      {isChoosable && (
        <span className="plan-comparison-head-choose" aria-hidden>
          {t("planComparison.headerChoose")}
          <ChevronRight className="plan-comparison-head-choose-icon" aria-hidden />
        </span>
      )}
    </>
  );

  if (isChoosable) {
    return (
      <div className={headerClass}>
        <button
          type="button"
          className="plan-comparison-col-button plan-comparison-col-choose"
          aria-label={t("planComparison.chooseShort", { plan: planLabel })}
          // The label replaces the content for a screen reader; the tagline and price, shown
          // only here, stay readable as the description.
          aria-describedby={`plan-comparison-${plan}-tagline plan-comparison-${plan}-price`}
          disabled={disabled}
          onClick={onChoose}
        >
          {headerBody}
        </button>
      </div>
    );
  }

  if (isSelectable) {
    return (
      <div className={headerClass}>
        <button
          type="button"
          role="radio"
          aria-checked={!!selected}
          data-selected={selected}
          className="plan-comparison-col-button"
          disabled={disabled}
          onClick={onSelect}
        >
          {headerBody}
        </button>
      </div>
    );
  }

  return (
    <div className={headerClass}>
      <div className="plan-comparison-col-inner">{headerBody}</div>
    </div>
  );
}

/**
 * Price, billing note and annual savings for one plan. Spans only, so it can sit inside the
 * plans page's whole-card buttons as well as the integrated column headers.
 */
export function PlanPriceLines({
  plan,
  interval,
  offerFirstCents,
  installmentsFirst,
}: {
  plan: Plan;
  interval: BillingInterval;
  /** First-month offer: today's charge, with the list price struck through. */
  offerFirstCents?: number;
  /**
   * Plans page: the Brazilian annual price leads with the card's 12 interest-free
   * instalments ("12x R$ 35") and the saving in reais, which is how buyers here
   * compare it with the monthly price.
   */
  installmentsFirst?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const currency = currencyFromLocale(i18n.language);
  const money = (cents: number) => formatMoney(cents, i18n.language, currency);
  const def = getPlanPricing(plan, interval, currency);
  if (installmentsFirst && interval === "annual" && offerFirstCents == null && currency === "BRL") {
    const savings = getAnnualSavings(plan, currency);
    return (
      <>
        <span className="plan-comparison-price">
          <span className="plan-comparison-price-installments">{STONE_INSTALLMENT_COUNT}x</span>
          <span className="plan-comparison-price-amount">{money(getAnnualInstallmentCents(plan, currency))}</span>
        </span>
        <span className="plan-comparison-billing-note">
          {t("plansPage.annualCardNote", { total: money(def.totalCents) })}
        </span>
        {savings.savingsCents > 0 && (
          <span className="plan-comparison-savings plan-comparison-savings-chip">
            {t("plansPage.saveYear", { amount: money(savings.savingsCents) })}
          </span>
        )}
      </>
    );
  }
  const headlineCents = offerFirstCents != null ? offerFirstCents : def.monthlyEquivalentCents;
  const savings = getAnnualSavings(plan, currency);
  return (
    <>
      <span className="plan-comparison-price">
        {offerFirstCents != null && def.monthlyEquivalentCents > offerFirstCents && (
          <span className="plan-comparison-price-list">{money(def.monthlyEquivalentCents)}</span>
        )}
        <span className="plan-comparison-price-amount">{money(headlineCents)}</span>
        <span className="plan-comparison-price-period">
          {offerFirstCents != null ? t("checkout.offerToday") : t("plans.perMonth")}
        </span>
      </span>
      <span className="plan-comparison-billing-note">
        {offerFirstCents != null
          ? t("checkout.offerThenShort", { amount: money(def.totalCents) })
          : interval === "annual"
            ? `${t("plansPage.billedAnnually")} · ${money(def.totalCents)}${t("plans.perYear")}`
            : t("plansPage.billedMonthly")}
      </span>
      {interval === "annual" && offerFirstCents == null && savings.savingsCents > 0 && (
        <span className="plan-comparison-savings">{t("plans.saveUpTo", { percentage: savings.percentage })}</span>
      )}
    </>
  );
}

function PlanComparisonCell({
  value,
  displayType,
  featured,
  selected,
}: {
  value: ReturnType<typeof getPlanComparisonValue>;
  displayType: "boolean" | "limit" | "text";
  featured?: boolean;
  selected?: boolean;
}) {
  const { t } = useTranslation();
  const label = formatPlanComparisonCellValue(value, t);
  const positive = planComparisonCellIsPositive(value);

  if (displayType === "boolean") {
    const Icon = positive ? Check : X;
    return (
      <td
        className={`plan-comparison-cell plan-comparison-cell-boolean ${positive ? "plan-comparison-cell-yes" : "plan-comparison-cell-no"} ${featured ? "plan-comparison-cell-featured" : ""} ${selected ? "plan-comparison-cell-selected" : ""}`}
      >
        <span className="plan-comparison-cell-icon" aria-hidden>
          <Icon className="w-4 h-4" strokeWidth={positive ? 3 : 2.5} />
        </span>
        <span className="sr-only">{label}</span>
      </td>
    );
  }

  if (value.kind === "excluded") {
    return (
      <td
        className={`plan-comparison-cell plan-comparison-cell-no ${featured ? "plan-comparison-cell-featured" : ""} ${selected ? "plan-comparison-cell-selected" : ""}`}
      >
        <span className="plan-comparison-cell-icon" aria-hidden>
          <Minus className="w-4 h-4" strokeWidth={2.5} />
        </span>
        <span className="plan-comparison-cell-text">{label}</span>
      </td>
    );
  }

  return (
    <td
      className={`plan-comparison-cell plan-comparison-cell-text-value ${positive ? "plan-comparison-cell-yes" : ""} ${featured ? "plan-comparison-cell-featured" : ""} ${selected ? "plan-comparison-cell-selected" : ""}`}
    >
      {positive && displayType === "limit" && value.kind === "unlimited" && (
        <span className="plan-comparison-cell-icon plan-comparison-cell-icon-inline" aria-hidden>
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </span>
      )}
      <span>{label}</span>
    </td>
  );
}

export function PlanIntervalToggle({
  value,
  onChange,
  disabled,
}: {
  value: BillingInterval;
  onChange: (v: BillingInterval) => void;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const premiumSavings = getAnnualSavings("premium", currencyFromLocale(i18n.language)).percentage;

  return (
    <div
      className={`seg ${disabled ? "opacity-60 pointer-events-none" : ""}`}
      role="tablist"
      aria-label="billing interval"
    >
      <button
        type="button"
        role="tab"
        aria-selected={value === "monthly"}
        data-active={value === "monthly"}
        onClick={() => onChange("monthly")}
        disabled={disabled}
      >
        {t("plans.monthly")}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "annual"}
        data-active={value === "annual"}
        onClick={() => onChange("annual")}
        disabled={disabled}
      >
        {t("plans.annual")}
        <span className="seg-save-badge" aria-label={t("plans.saveUpTo", { percentage: premiumSavings })}>
          -{premiumSavings}%
        </span>
      </button>
    </div>
  );
}
